import React, { useState, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { FiSave, FiArrowLeft } from 'react-icons/fi';
import Layout from '../../../components/admin/Layout';
import IconPicker from '../../../components/admin/IconPicker';
import axios from '../../../utils/axios';
import ToggleSwitch from '../../../components/common/ToggleSwitch';
import { LanguageContext } from '../../../context/LanguageContext';
import { adminTranslations } from '../../../translations/admin';

const CreateCategory = () => {
    const navigate = useNavigate();
    const { language } = useContext(LanguageContext);
    const isRTL = language === 'ar';
    
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [formData, setFormData] = useState({
        name: '',
        name_ar: '',
        slug: '',
        sort_order: 0,
        is_active: true,
        icon: ''
    });

    const t = (key) => {
        return adminTranslations[language]?.[key] || key;
    };

    const generateSlug = useCallback((name) => {
        return name
            .toLowerCase()
            .replace(/[^a-z0-9-]/g, '-')
            .replace(/-+/g, '-')
            .replace(/^-|-$/g, '');
    }, []);

    const handleNameChange = useCallback((e) => {
        const { value } = e.target;
        setFormData(prev => ({
            ...prev,
            name: value,
            slug: generateSlug(value)
        }));
    }, [generateSlug]);

    const handleChange = useCallback((e) => {
        const { name, value, type, checked } = e.target;
        
        if (type === 'checkbox') {
            setFormData(prev => ({
                ...prev,
                [name]: checked
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }

        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    }, [errors]);

    const handleIconChange = useCallback((iconName) => {
        setFormData(prev => ({ ...prev, icon: iconName }));
        if (errors.icon) {
            setErrors(prev => ({ ...prev, icon: null }));
        }
    }, [errors]);

    const handleSlugChange = useCallback((e) => {
        const { value } = e.target;
        setFormData(prev => ({
            ...prev,
            slug: generateSlug(value)
        }));
    }, [generateSlug]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setErrors({});

        const createPromise = new Promise(async (resolve, reject) => {
            try {
                const form = new FormData();
                
                Object.keys(formData).forEach(key => {
                    if (formData[key] !== null && formData[key] !== '') {
                        form.append(key, formData[key]);
                    }
                });

                await axios.post('/categories', form);
                resolve();
                navigate('/admin/categories');
            } catch (error) {
                if (error.response?.status === 422) {
                    setErrors(error.response.data.errors);
                    reject(new Error(t('checkFormErrors')));
                } else {
                    reject(new Error(error.response?.data?.message || t('errorCreatingCategory')));
                }
            } finally {
                setLoading(false);
            }
        });

        toast.promise(createPromise, {
            loading: t('saving'),
            success: t('categoryCreated'),
            error: (err) => err.message,
        });
    };

    return (
        <Layout>
            <div className={`max-w-6xl mx-auto px-4 py-8 ${isRTL ? 'rtl' : ''}`}>
                <button
                    onClick={() => navigate('/admin/categories')}
                    className={`inline-flex items-center text-gray-600 hover:text-gray-900 mb-6 group
                        ${isRTL ? 'flex-row-reverse' : ''}`}
                >
                    <FiArrowLeft 
                        className={`h-4 w-4 transition-transform group-hover:translate-x-[-4px]
                        ${isRTL ? 'ml-2 rotate-180' : 'mr-2'}`} 
                    />
                    {t('backToCategories')}
                </button>

                <div className="bg-gradient-to-r from-yellow-50 to-yellow-100 rounded-2xl p-6 mb-8">
                    <h1 className="text-2xl font-bold text-gray-800 mb-2">{t('createCategory')}</h1>
                    <p className="text-gray-600">{t('createCategoryDesc')}</p>
                </div>

                {errors.general && (
                    <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded-lg">
                        <p className="text-red-700">{errors.general}</p>
                    </div>
                )}

                <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
                    <form onSubmit={handleSubmit}>
                        {/* Header Section */}
                        <div className="px-6 py-4 border-b border-gray-100">
                            <div className="flex items-center justify-between">
                                <h2 className="text-xl font-semibold text-gray-800">
                                    {t('createCategory')}
                                </h2>
                                <ToggleSwitch
                                    checked={formData.is_active}
                                    onChange={handleChange}
                                    name="is_active"
                                    label={t('status')}
                                />
                            </div>
                        </div>

                        <div className="p-6 space-y-8">
                            {/* Basic Information */}
                            <div className="space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-6">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                {t('nameEnglish')}
                                            </label>
                                            <input
                                                type="text"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleNameChange}
                                                required
                                                className={`w-full px-4 py-2.5 rounded-lg border ${
                                                    errors.name ? 'border-red-500' : 'border-gray-200'
                                                } bg-white focus:border-yellow-500 focus:ring-2 focus:ring-yellow-200 
                                                transition-all duration-200 text-gray-900 text-base`}
                                            />
                                            {errors.name && <p className="mt-1.5 text-sm text-red-500">{errors.name[0]}</p>}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                {t('categorySlug')}
                                            </label>
                                            <input
                                                type="text"
                                                name="slug"
                                                value={formData.slug}
                                                onChange={handleSlugChange}
                                                required
                                                className={`w-full px-4 py-2.5 rounded-lg border ${
                                                    errors.slug ? 'border-red-500' : 'border-gray-200'
                                                } bg-white focus:border-yellow-500 focus:ring-2 focus:ring-yellow-200 
                                                transition-all duration-200 text-gray-900 text-base`}
                                            />
                                            {errors.slug && <p className="mt-1.5 text-sm text-red-500">{errors.slug[0]}</p>}
                                        </div>
                                    </div>

                                    <div className="space-y-6">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                {t('nameArabic')}
                                            </label>
                                            <input
                                                type="text"
                                                name="name_ar"
                                                value={formData.name_ar}
                                                onChange={handleChange}
                                                dir="rtl"
                                                required
                                                className={`w-full px-4 py-2.5 rounded-lg border ${
                                                    errors.name_ar ? 'border-red-500' : 'border-gray-200'
                                                } bg-white focus:border-yellow-500 focus:ring-2 focus:ring-yellow-200 
                                                transition-all duration-200 text-gray-900 text-base`}
                                            />
                                            {errors.name_ar && <p className="mt-1.5 text-sm text-red-500">{errors.name_ar[0]}</p>}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                {t('sortOrder')}
                                            </label>
                                            <input
                                                type="number"
                                                name="sort_order"
                                                value={formData.sort_order}
                                                onChange={handleChange}
                                                className={`w-full px-4 py-2.5 rounded-lg border ${
                                                    errors.sort_order ? 'border-red-500' : 'border-gray-200'
                                                } bg-white focus:border-yellow-500 focus:ring-2 focus:ring-yellow-200 
                                                transition-all duration-200 text-gray-900 text-base`}
                                            />
                                            {errors.sort_order && <p className="mt-1.5 text-sm text-red-500">{errors.sort_order[0]}</p>}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Media Section */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                {/* Icon Picker */}
                                <div className="md:col-span-1">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {t('categoryIcon')}
                                    </label>
                                    <div className={`${isRTL ? 'rtl' : ''} bg-gray-50 rounded-lg p-4`}>
                                        <IconPicker
                                            value={formData.icon}
                                            onChange={handleIconChange}
                                            error={errors.icon && errors.icon[0]}
                                        />
                                    </div>
                                    {errors.icon && <p className="mt-1.5 text-sm text-red-500">{errors.icon[0]}</p>}
                                </div>
                            </div>
                        </div>

                        {/* Footer Actions */}
                        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 rounded-b-2xl">
                            <div className="flex items-center justify-between">
                                <button
                                    type="button"
                                    onClick={() => navigate('/admin/categories')}
                                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                                >
                                    <FiArrowLeft className={isRTL ? 'ml-2' : 'mr-2'} />
                                    {t('cancel')}
                                </button>

                                <button
                                    type="submit"
                                    disabled={loading}
                                    className={`inline-flex items-center px-6 py-3 bg-yellow-500 text-white rounded-lg
                                        hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-500 
                                        focus:ring-offset-2 transform transition-all duration-200
                                        ${loading ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-md active:scale-95'}`}
                                >
                                    <FiSave className={isRTL ? 'ml-2' : 'mr-2'} />
                                    {loading ? t('saving') : t('save')}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </Layout>
    );
};

export default CreateCategory;
